import { gridLayout } from './layout';
import type { GameApp } from './app';
import type { Screen } from './mode';
import type { ArtworkDetail, ArtworkSummary } from '../shared/artworks';
import type { PublicTurn } from '../modes/draw-guess/types';
import { renderDrawing, renderDrawingProgress, replayDuration } from '../shared/art/drawing';
import { C, T } from './paint';
import { ApiError } from './platform';
import type { Pointer } from './platform';

type LoadFailure = { attempts: number; retryAt: number };

export const legacyArtwork = (turn: PublicTurn): ArtworkSummary => ({
  id: turn.id, mode: 'draw-guess', artistId: turn.artistId, artistName: '', sourceName: '友友画猜',
  word: turn.word ?? '', number: turn.number, createdAt: turn.createdAt, completed: true,
  likeCount: turn.likeCount ?? 0, likedByMe: turn.likedByMe ?? false, hasGuessReplay: turn.hasReplay ?? false,
});

export class GalleryClient {
  items: ArtworkSummary[] = [];
  artwork: ArtworkDetail | null = null;
  private pageCapacity = 4;
  filter: 'all' | 'mine' | 'liked' = 'all';
  loading = false;
  returnTo: Screen = 'gallery';
  replayStarted = 0;
  replaySpeed = 1;
  replayFinished = false;
  replayPaused = false;
  pausedElapsed = 0;
  board = { x: 24, y: 124, size: 342 };
  /** 进度轨的命中区（逻辑坐标）：渲染时写入，pointer() 据此判断拖动。 */
  trackZone: { x0: number; x1: number; y: number; h: number } | null = null;
  private scrubbing = false;
  private scrubWasPlaying = false;
  private gesture: { x0: number; y0: number; moved: boolean } | null = null;
  private overlayGlyph: 'play' | 'pause' | null = null;
  private overlayAt = 0;
  private slideDir = 0;
  private slideAt = 0;
  private hintAt = 0;
  private generation = 0;
  private thumbnails = new Map<string, HTMLCanvasElement>();
  private pending = new Map<string, Promise<ArtworkDetail>>();
  private failed = new Map<string, LoadFailure>();
  private cooldownUntil = 0;
  private requestTimes: number[] = [];
  private visibleKey = '';
  private pageReadyAt = 0;
  private listPending = false;
  private listFailure?: LoadFailure;
  private details = new Map<string, ArtworkDetail>();
  private buffer: HTMLCanvasElement;
  private fullKey = '';

  constructor(readonly host: GameApp) { this.buffer = host.platform.createCanvas(1000, 1000); }
  private key(item: ArtworkSummary) { return `${item.mode}:${item.id}`; }
  private path(item: ArtworkSummary) { return `/artworks/${item.mode}/${item.id}`; }
  navigate(dir: 1 | -1 = 1) {
    this.host.page = 0; this.host.go('gallery', dir); this.loading = true;
    this.generation++; this.listPending = false; this.listFailure = undefined; this.visibleKey = '';
    this.details.clear(); this.thumbnails.clear(); this.failed.clear();
    this.loadList();
  }
  private current(generation: number, token: string) { return generation === this.generation && token === this.host.token; }
  /** Allow a whole page to start together, while leaving room in the server's 180/minute budget. */
  private requestReadyAt() {
    const now = Date.now();
    this.requestTimes = this.requestTimes.filter(time => time > now - 60_000);
    return Math.max(this.cooldownUntil, this.requestTimes.length >= 120 ? this.requestTimes[0] + 60_000 : 0);
  }
  private failure(error: unknown, previous?: LoadFailure): LoadFailure {
    const now = Date.now(); const status = error instanceof ApiError ? error.status : 0;
    const attempts = (previous?.attempts ?? 0) + 1;
    let retryAt = Infinity;
    if (status === 429) {
      const delay = error instanceof ApiError && Number.isFinite(error.retryAfterMs) ? error.retryAfterMs! : 60_000;
      this.cooldownUntil = Math.max(this.cooldownUntil, now + Math.max(1000, delay));
      retryAt = this.cooldownUntil;
    } else if (status === 0 || status === 408 || status >= 500) {
      retryAt = now + Math.min(30_000, 2000 * 2 ** Math.min(attempts - 1, 4));
    }
    return { attempts, retryAt };
  }
  private loadList() {
    if (!this.loading || this.listPending || Date.now() < Math.max(this.requestReadyAt(), this.listFailure?.retryAt ?? 0)) return;
    const generation = this.generation; const token = this.host.token; this.listPending = true;
    this.requestTimes.push(Date.now());
    void this.host.api<{ items: ArtworkSummary[] }>('/artworks').then(result => {
      if (!this.current(generation, token)) return;
      this.items = result.items; this.loading = false; this.listFailure = undefined;
    }).catch(error => {
      if (!this.current(generation, token)) return;
      this.listFailure = this.failure(error, this.listFailure);
    }).finally(() => { if (this.current(generation, token)) this.listPending = false; });
  }
  clear() {
    this.generation++; this.items = []; this.artwork = null; this.loading = false; this.listPending = false;
    this.details.clear(); this.thumbnails.clear(); this.failed.clear(); this.listFailure = undefined; this.visibleKey = '';
    this.gesture = null; this.scrubbing = false; this.trackZone = null; this.overlayGlyph = null; this.hintAt = 0;
  }
  back() {
    this.host.screen = this.returnTo;
    if (this.returnTo === 'session') void this.host.run(() => this.host.refresh());
  }
  replay() { this.replayStarted = Date.now(); this.replaySpeed = 1; this.replayFinished = false; this.replayPaused = false; this.pausedElapsed = 0; this.fullKey = ''; }
  finishReplay() { this.replayFinished = true; this.replayPaused = false; this.fullKey = ''; }
  /** 当前回放进度（毫秒）：暂停 / 拖动时冻结在 pausedElapsed。 */
  elapsedAt(now: number) { return this.replayPaused ? this.pausedElapsed : (now - this.replayStarted) * this.replaySpeed; }
  /** 点按画作：播放中暂停、暂停中续播、播完后再看一遍；中央图标随状态闪现。 */
  togglePause() {
    const now = Date.now();
    if (this.replayFinished) { this.replay(); this.overlayGlyph = 'play'; this.overlayAt = now; return; }
    if (this.replayPaused) { this.replayStarted = now - this.pausedElapsed / this.replaySpeed; this.replayPaused = false; this.overlayGlyph = 'play'; }
    else { this.pausedElapsed = this.elapsedAt(now); this.replayPaused = true; this.overlayGlyph = 'pause'; }
    this.overlayAt = now;
  }
  /** 拖动进度轨：画面冻结在拖动点，抬手后按拖动前的播放状态决定续播或停住。 */
  private seekX(x: number) {
    const t = this.trackZone; const art = this.artwork; if (!t || !art) return;
    const duration = Math.max(1, replayDuration(art.drawing));
    const ratio = Math.max(0, Math.min(1, (x - t.x0) / (t.x1 - t.x0)));
    this.replayPaused = true; this.pausedElapsed = ratio * duration; this.replayFinished = ratio >= 1; this.fullKey = '';
  }
  /** 画廊当前筛选下的作品序列：详情页在它的相邻作品之间滑动切换。 */
  private navList() { return this.items.filter(item => this.filter === 'mine' ? item.artistId === this.host.player?.id : this.filter === 'liked' ? item.likedByMe : true); }
  private navIndex() { const art = this.artwork; return art ? this.navList().findIndex(item => this.key(item) === this.key(art)) : -1; }
  navigateArt(dir: 1 | -1) {
    if (this.host.busy) return;
    const list = this.navList(); const idx = this.navIndex(); if (idx < 0) return;
    const next = list[idx + dir]; if (!next) return;
    const cached = this.details.get(this.key(next));
    if (cached) this.show(cached, this.returnTo, dir);
    else this.open(next, this.returnTo);
  }
  /** 详情页手势：进度轨拖动 = 拖进度；画作上左右滑 = 换上一幅 / 下一幅；点按 = 播放 / 暂停。
   *  浮层控件（点赞胶囊、左右箭头）不吞事件，交回普通命中派发。返回 true 表示已消费。 */
  pointer(kind: 'down' | 'move' | 'up' | 'cancel', point: Pointer): boolean {
    if (this.host.screen !== 'artwork' || !this.artwork || this.host.busy) { this.gesture = null; this.scrubbing = false; return false; }
    const { x, y } = point;
    if (kind === 'down') {
      const onFloat = this.host.paint.hits.some(hit => ['art-like', 'art-prev', 'art-next'].includes(hit.id) && !hit.disabled && x >= hit.x && x <= hit.x + hit.w && y >= hit.y && y <= hit.y + hit.h);
      if (onFloat) return false;
      const t = this.trackZone;
      if (t && x >= t.x0 - 10 && x <= t.x1 + 10 && y >= t.y && y <= t.y + t.h) {
        this.scrubbing = true; this.scrubWasPlaying = !this.replayPaused && !this.replayFinished; this.seekX(x); return true;
      }
      const { x: bx, y: by, size } = this.board;
      if (x >= bx - 10 && x <= bx + size + 10 && y >= by - 10 && y <= by + size + 10) { this.gesture = { x0: x, y0: y, moved: false }; return true; }
      return false;
    }
    if (this.scrubbing) {
      if (kind === 'move') { this.seekX(x); return true; }
      this.scrubbing = false;
      if (kind !== 'cancel') this.seekX(x);
      if (this.scrubWasPlaying && !this.replayFinished) { this.replayPaused = false; this.replayStarted = Date.now() - this.pausedElapsed / this.replaySpeed; }
      return true;
    }
    if (this.gesture) {
      const dx = x - this.gesture.x0; const dy = y - this.gesture.y0;
      if (kind === 'move') { if (Math.abs(dx) > 8 || Math.abs(dy) > 8) this.gesture.moved = true; return true; }
      const moved = this.gesture.moved; this.gesture = null;
      if (kind === 'cancel') return true;
      if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.2) this.navigateArt(dx < 0 ? 1 : -1);
      else if (!moved) this.togglePause();
      return true;
    }
    return false;
  }
  private warm(item: ArtworkSummary) {
    const key = this.key(item); const requestKey = `${this.generation}:${this.host.token}:${key}`;
    if (this.details.has(key) || this.pending.has(requestKey) || this.pending.size >= 2) return;
    if (Date.now() < Math.max(this.requestReadyAt(), this.failed.get(key)?.retryAt ?? 0)) return;
    void this.loadDetail(item).catch(() => {});
  }
  private show(art: ArtworkDetail, returnTo: Screen, dir: 1 | -1 | 0 = 0) {
    this.artwork = art; this.returnTo = returnTo; this.replay();
    this.replaySpeed = Math.max(1, replayDuration(art.drawing) / 10000);
    this.slideDir = dir; this.slideAt = dir ? Date.now() : 0;
    // 前三次打开给一次手势引导（点按暂停 / 拖进度 / 滑动换画），之后不再打扰。
    const seen = this.host.platform.get('artworkHintSeen', 0);
    this.hintAt = seen < 3 ? Date.now() : 0;
    if (seen < 3) this.host.platform.set('artworkHintSeen', seen + 1);
    const list = this.navList(); const idx = list.findIndex(item => this.key(item) === this.key(art));
    for (const item of [list[idx - 1], list[idx + 1]]) if (idx >= 0 && item) this.warm(item);
    this.host.screen = 'artwork';
  }
  openLegacy(turn: PublicTurn, returnTo: Screen) {
    const item = this.cacheLegacy(turn);
    if (turn.drawing) this.show(this.details.get(this.key(item))!, returnTo);
    else this.open(item, returnTo);
  }
  cacheLegacy(turn: PublicTurn) {
    const item = legacyArtwork(turn);
    if (turn.drawing) this.details.set(this.key(item), { ...item, drawing: turn.drawing });
    return item;
  }
  open(item: ArtworkSummary, returnTo: Screen = 'gallery') {
    const key = this.key(item);
    const readyAt = this.requestReadyAt();
    if (!this.details.has(key) && !this.pending.has(`${this.generation}:${this.host.token}:${key}`) && Date.now() < readyAt) {
      this.host.toast(`稍等 ${Math.ceil((readyAt - Date.now()) / 1000)} 秒，画作会自动重试。`); return;
    }
    const generation = this.generation; const token = this.host.token; const screen = this.host.screen;
    void this.host.run(async () => {
      try {
        const detail = this.details.get(key) ?? await this.loadDetail(item);
        if (this.current(generation, token) && this.host.screen === screen) this.show(detail, returnTo);
      } catch (error) {
        if (this.current(generation, token) && this.host.screen === screen) throw error;
      }
    });
  }
  private loadDetail(item: ArtworkSummary): Promise<ArtworkDetail> {
    const key = this.key(item); const requestKey = `${this.generation}:${this.host.token}:${key}`;
    const pending = this.pending.get(requestKey); if (pending) return pending;
    const generation = this.generation; const token = this.host.token;
    this.requestTimes.push(Date.now());
    const request = this.host.api<ArtworkDetail>(this.path(item)).then(art => {
      if (this.current(generation, token)) {
        if (this.details.size >= 16 && !this.details.has(key)) this.details.delete(this.details.keys().next().value!);
        this.details.set(key, art); this.failed.delete(key);
      }
      return art;
    }).catch(error => {
      if (this.current(generation, token)) this.failed.set(key, this.failure(error, this.failed.get(key)));
      throw error;
    }).finally(() => { this.pending.delete(requestKey); });
    this.pending.set(requestKey, request); return request;
  }
  private thumbnail(item: ArtworkSummary, prefetch = false) {
    const key = this.key(item); const cached = this.thumbnails.get(key);
    if (cached) {
      if (!prefetch) { this.thumbnails.delete(key); this.thumbnails.set(key, cached); }
      return cached;
    }
    const detail = this.details.get(key);
    if (detail) {
      const canvas = this.host.platform.createCanvas(384, 384);
      renderDrawing(canvas.getContext('2d')!, detail.drawing, 384);
      if (this.thumbnails.size >= 32) this.thumbnails.delete(this.thumbnails.keys().next().value!);
      this.thumbnails.set(key, canvas); return canvas;
    }
    // Warm only the next page. Keep two slots and the last 20 reads for visible/user work.
    const readyAt = Math.max(this.pageReadyAt, this.requestReadyAt(), this.failed.get(key)?.retryAt ?? 0);
    if (this.pending.size < (prefetch ? 2 : 4) && (!prefetch || this.requestTimes.length < 100) && !this.pending.has(`${this.generation}:${this.host.token}:${key}`) && Date.now() >= readyAt) {
      void this.loadDetail(item).catch(() => {});
    }
    return undefined;
  }
  resize() {
    this.gesture=null;
    if(this.scrubbing){this.scrubbing=false;if(this.scrubWasPlaying){this.replayStarted=Date.now()-this.pausedElapsed/this.replaySpeed;this.replayPaused=false;}}
  }
  screen() {
    this.loadList();
    const p = this.host.paint; const h = this.host.H - this.host.top;
    this.host.header('画廊', undefined, false); this.host.starJar(258, 19, 'right');
    const filters = [['all', '全部', 70], ['mine', '仅看自己', 92], ['liked', '点赞过的画', 128]] as const;
    let x = 24;
    for (const [id, label, width] of filters) {
      p.button(`gallery-${id}`, label, x, 92, width, 34, () => { this.filter = id; this.host.page = 0; }, this.filter === id ? 'secondary' : 'plain'); x += width + 8;
    }
    const items = this.items.filter(item => this.filter === 'mine' ? item.artistId === this.host.player?.id : this.filter === 'liked' ? item.likedByMe : true);
    const navY = h - 74;
    const grid = gridLayout(this.host.W, this.host.tablet, 140, navY - 48);
    const { cardHeight: cardH, cardWidth, columns, gap, capacity } = grid;
    if (capacity !== this.pageCapacity) { this.host.page = Math.floor(this.host.page * this.pageCapacity / capacity); this.pageCapacity = capacity; }
    const gridBottom = navY - 48;
    this.host.page = Math.min(this.host.page, Math.max(0, Math.ceil(items.length / capacity) - 1));
    const visible = items.slice(this.host.page * capacity, (this.host.page + 1) * capacity);
    const visibleKey = `${this.filter}:${this.host.page}:${visible.map(item => this.key(item)).join(',')}`;
    if (visibleKey !== this.visibleKey) { this.visibleKey = visibleKey; this.pageReadyAt = Date.now() + 100; }
    if (!items.length || this.loading) {
      const cy = Math.min(250, (h - 180) / 2); p.doodle(p.x(195), cy, 0.8);
      p.text(this.loading ? this.loadLabel(this.listFailure, this.listPending) : '还没有收藏的画作', p.x(195), cy + 105, T.section, C.ink, '600', 'center');
      if (this.listFailure?.retryAt === Infinity) p.button('gallery-retry', '重新加载画廊', p.x(95), cy + 130, p.span(200), 38, () => this.navigate(), 'secondary');
    } else visible.forEach((item, i) => {
      const x = 24 + (i % columns) * (cardWidth + gap); const y = 140 + Math.floor(i / columns) * (cardH + gap);
      p.card(x, y, cardWidth, cardH, 8);
      const imageSize = Math.max(36, Math.min(cardWidth - 18, cardH - 72)); const imageX = x + (cardWidth - imageSize) / 2;
      const image = this.thumbnail(item);
      if (image) p.ctx.drawImage(image, imageX, y + 8, imageSize, imageSize);
      else p.text(this.loadLabel(this.failed.get(this.key(item)), this.pending.has(`${this.generation}:${this.host.token}:${this.key(item)}`)), x + cardWidth / 2, y + imageSize / 2, T.note, C.mute, '400', 'center');
      p.fit(item.word, x + 12, y + cardH - 46, cardWidth - 26, T.bodyStrong, C.ink, '600');
      p.fit(item.mode === 'live-draw' ? `画猜派对${item.completed ? '' : ' · 未完成'}` : '友友画猜', x + 12, y + cardH - 27, cardWidth - 26, T.tiny, item.mode === 'live-draw' ? C.accentDeep : C.mute);
      p.fit(item.artistId === this.host.player?.id ? '我画的' : item.artistName || '友友画的', x + 12, y + cardH - 12, cardWidth - 64, T.tiny, C.mute);
      if (item.likeCount) { p.icon('heart', x + cardWidth - 34, y + cardH - 13, 10, C.red); p.text(String(item.likeCount), x + cardWidth - 13, y + cardH - 12, T.tiny, C.red, '600', 'right'); }
      p.hits.push({ id: `art-${item.id}`, label: `查看作品 ${item.word}`, x, y, w: cardWidth, h: cardH, action: () => this.open(item) });
    });
    if (this.host.screen === 'gallery' && !this.host.busy && !this.loading && visible.length && Date.now() >= this.pageReadyAt && visible.every(item => this.thumbnails.has(this.key(item)))) {
      for (const item of items.slice((this.host.page + 1) * capacity, (this.host.page + 2) * capacity)) this.thumbnail(item, true);
    }
    if (items.length > capacity) this.host.paging(items.length, capacity, gridBottom + 8);
    this.host.navigation('gallery');
  }
  private loadLabel(failure?: LoadFailure, loading = false) {
    if (loading) return '正在加载';
    const readyAt = this.requestReadyAt();
    if (Date.now() < readyAt) return `${Math.ceil((readyAt - Date.now()) / 1000)} 秒后重试`;
    if (failure?.retryAt === Infinity) return '点开重试';
    if (failure && Date.now() < failure.retryAt) return '连接恢复后重试';
    return '正在加载';
  }
  artworkScreen(now: number) {
    const art = this.artwork; if (!art) { this.host.screen = 'gallery'; return; }
    const p = this.host.paint; const h = this.host.H - this.host.top;
    // 题目即页名：页头直接展示作品题目，作者 / 来源 / 日期收进副标一行，不再在画作上方重复堆砌。
    const created = art.createdAt > 946684800000 ? new Date(art.createdAt) : null;
    const artist = art.artistId === this.host.player?.id ? '我画的' : art.artistName.length > 8 ? `${art.artistName.slice(0, 8)}…` : art.artistName;
    const meta = [artist, art.sourceName, created ? `${created.getMonth() + 1}月${created.getDate()}日` : '', art.completed ? '' : '未完成'].filter(Boolean).join(' · ');
    this.host.header(art.word || '一幅共同的回忆', meta || undefined);
    // 竖向预算：页头 104 + 卡纸 20 + 播放器 64 + 操作行 42/40 与行距 + 底部 16；高屏把余量三七分。
    const fixed = art.hasGuessReplay ? 306 : 256;
    const size = Math.max(120, Math.min(this.host.W - 48, h - fixed));
    const x = (this.host.W - size) / 2; const y = 104 + Math.max(0, Math.min(48, (h - fixed - size) * 0.35));
    this.board = { x, y, size };
    const duration = Math.max(1, replayDuration(art.drawing));
    const elapsed = Math.min(this.elapsedAt(now), duration);
    if (!this.replayPaused && this.elapsedAt(now) >= duration) this.replayFinished = true;
    if (this.replayFinished) {
      if (this.fullKey !== this.key(art)) { renderDrawing(this.buffer.getContext('2d')!, art.drawing, 1000); this.fullKey = this.key(art); }
    } else { renderDrawingProgress(this.buffer.getContext('2d')!, art.drawing, 1000, elapsed); this.fullKey = ''; }
    // 画作本体是一整块沉浸区域：点按播放 / 暂停，左右滑切换相邻作品；切换时整卡滑入。
    const slideAge = now - this.slideAt; const sliding = this.slideDir !== 0 && slideAge < 220;
    const dx = sliding ? (1 - slideAge / 220) ** 2 * 70 * this.slideDir : 0;
    const g = p.ctx; g.save(); if (dx) g.translate(dx, 0);
    p.card(x - 10, y - 10, size + 20, size + 20, 16);
    p.box(x - 2, y - 2, size + 4, size + 4, C.white, 4, C.line);
    g.drawImage(this.buffer, x, y, size, size);
    p.tape(x + size - 20, y - 12, 0.5, 56, 15);
    p.hits.push({ id: 'art-board', label: '画作：点按暂停或播放，左右滑动切换作品', x: x - 10, y: y - 10, w: size + 20, h: size + 20, action: () => {} });
    // 相邻作品存在时露出半透明箭头：点按与滑动等效（从会话历史等单幅入口进来时不显示）。
    const list = this.navList(); const idx = this.navIndex();
    const chevron = (id: string, label: string, glyph: string, cx: number, dir: 1 | -1) => {
      g.save(); g.globalAlpha = 0.55; p.circle(cx, y + size / 2, 16, C.white, undefined, 3); g.restore();
      g.save(); g.globalAlpha = 0.78; p.text(glyph, cx, y + size / 2 - 1, 20, C.ink, '600', 'center'); g.restore();
      p.hits.push({ id, label, x: cx - 21, y: y + size / 2 - 21, w: 42, h: 42, action: () => this.navigateArt(dir) });
    };
    if (idx > 0) chevron('art-prev', `上一幅 ${list[idx - 1].word}`, '‹', x + 24, -1);
    if (idx >= 0 && idx < list.length - 1) chevron('art-next', `下一幅 ${list[idx + 1].word}`, '›', x + size - 24, 1);
    // 点按播放 / 暂停的状态闪现：墨色圆底 + 白图标，600ms 淡出。
    if (this.overlayGlyph) {
      const age = now - this.overlayAt;
      if (age >= 600) this.overlayGlyph = null;
      else {
        g.save(); g.globalAlpha = age < 300 ? 1 : 1 - (age - 300) / 300;
        p.circle(p.x(195), y + size / 2, 30, 'rgba(46, 53, 66, 0.55)');
        p.icon(this.overlayGlyph, p.x(195), y + size / 2, 26, C.white);
        g.restore();
      }
    }
    // 前三次打开的手势引导：卡在卡纸下缘，2.6 秒淡出。
    if (this.hintAt) {
      const age = now - this.hintAt;
      if (age >= 2600) this.hintAt = 0;
      else {
        const text = '点按暂停 · 拖动进度 · 左右滑换画';
        g.save(); g.globalAlpha = age < 2000 ? 0.88 : 0.88 * (1 - (age - 2000) / 600);
        g.font = `600 ${p.fontSize(T.note)}px "PingFang SC", "Microsoft YaHei", sans-serif`;
        const tw = g.measureText(text).width;
        this.host.paint.box(this.host.W / 2 - (tw + 28) / 2, y + size - 58, tw + 28, 24, 'rgba(46, 53, 66, 0.82)', 12);
        p.text(text, p.x(195), y + size - 45.5, T.note, C.white, '600', 'center');
        g.restore();
      }
    }
    g.restore();
    // 播放器：播放 / 暂停圆钮 + 可拖动进度轨 + 时间 + 倍速签 + 跳到结尾，一整条收齐。
    const barY = y + size + 10 + 12;
    p.box(p.x(24), barY, p.span(342), 52, C.cream, 26, undefined, 10);
    const playing = !this.replayPaused && !this.replayFinished;
    p.iconButton('art-play', playing ? '暂停' : this.replayFinished ? '再看一遍' : '继续播放', playing ? 'pause' : 'play', p.x(52), barY + 26, 16, () => this.togglePause(), 'secondary');
    const progress = this.replayFinished ? 1 : Math.min(1, elapsed / duration);
    const tx0 = p.x(84); const tx1 = p.x(214); const kx = tx0 + (tx1 - tx0) * progress;
    this.trackZone = { x0: tx0, x1: tx1, y: barY + 12, h: 28 };
    g.save(); g.lineCap = 'round';
    g.strokeStyle = C.line; g.lineWidth = 6; g.beginPath(); g.moveTo(tx0, barY + 26); g.lineTo(tx1, barY + 26); g.stroke();
    if (progress > 0) { g.strokeStyle = C.accent; g.beginPath(); g.moveTo(tx0, barY + 26); g.lineTo(kx, barY + 26); g.stroke(); }
    g.beginPath(); g.arc(kx, barY + 26, 5, 0, Math.PI * 2); g.fillStyle = C.white; g.fill(); g.strokeStyle = C.accentDeep; g.lineWidth = 2; g.stroke();
    g.restore();
    const clock = (ms: number, round: 'floor' | 'ceil') => { const s = round === 'ceil' ? Math.ceil(ms / 1000) : Math.floor(ms / 1000); return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`; };
    p.text(`${clock(elapsed, 'floor')}/${clock(duration, 'ceil')}`, tx1 + 8, barY + 26.5, T.tiny, C.mute);
    p.button('art-speed', `${Math.max(1, Math.round(this.replaySpeed))}×`, p.x(282), barY + 12, 38, 28, () => {
      const played = this.elapsedAt(Date.now());
      // 手动倍速 1→2→4→8 循环；自动倍速（非档位值）点一下回到 1。
      this.replaySpeed = this.replaySpeed === 1 ? 2 : this.replaySpeed === 2 ? 4 : this.replaySpeed === 4 ? 8 : 1;
      if (this.replayPaused) this.pausedElapsed = played; else this.replayStarted = Date.now() - played / this.replaySpeed;
    }, 'plain');
    p.iconButton('art-skip', '看完整画作', 'next', p.x(344), barY + 26, 14, () => this.finishReplay(), 'plain', this.replayFinished);
    // 主操作行：保存画作居中稍短，点赞与举报带文字分列左右；举报用红色线条警示三角。
    const liked = art.likedByMe;
    const rowA = barY + 62;
    p.button('art-like', `${liked ? '已点赞' : '点赞'} ${art.likeCount}`, p.x(24), rowA, p.span(100), 42, () => { void this.host.run(async () => {
      const result = await this.host.api<{ liked: boolean; likeCount: number }>(`${this.path(art)}/like`, 'POST', {});
      for (const item of [art, ...this.items.filter(item => this.key(item) === this.key(art))]) { item.likedByMe = result.liked; item.likeCount = result.likeCount; }
    }); }, liked ? 'secondary' : 'plain', false, 'heart', undefined, liked ? C.red : undefined);
    p.button('art-save', '保存画作', p.x(134), rowA, p.span(144), 42, () => { void this.host.run(async () => {
      const canvas = this.host.platform.createCanvas(1000, 1000); renderDrawing(canvas.getContext('2d')!, art.drawing, 1000); await this.host.platform.export(canvas);
    }); }, 'primary', false, 'download');
    p.button('art-report', '举报', p.x(288), rowA, p.span(78), 42, () => { void this.host.run(async () => {
      if (!await this.host.platform.confirm('举报这幅作品？', '我们会查看作品和绘画过程。')) return;
      await this.host.api(`${this.path(art)}/report`, 'POST', { reason: '不适当内容' }); this.host.toast('举报已提交。');
    }); }, 'plain', false, 'warn', undefined, C.red);
    const rowB = rowA + 52;
    if (art.hasGuessReplay) p.button('art-guess-replay', '回看猜题过程', p.x(24), rowB, p.span(342), 40, () => { void this.host.run(async () => {
      const turn = await this.host.api<PublicTurn>(`/turns/${art.id}/artwork`); await this.host.drawGuess.loadGuessReplay(turn, 'artwork');
    }); }, 'secondary', false, 'refresh');
  }
}
